<template>
  <v-container fluid>
    <v-card class="pa-4" elevation="2">
      <v-row class="align-center">
        <v-col class="headline mb-4">
          <h2>Documentación ({{ NomType }})</h2>
        </v-col>
        <v-col cols="12" sm="6" class="text-right">
          <v-btn color="primary" @click="openCreateModal">Nuevo documento</v-btn>

          <!-- Modal crear/editar -->
          <v-dialog v-model="createDialog" max-width="700">
            <v-card>
              <v-card-title class="headline">
                {{ isEditing ? 'Editar documento' : 'Nuevo documento' }}
              </v-card-title>

              <v-card-text>
                <v-row>
                  <v-col cols="12" sm="4">
                    <!-- Client: desplegable que mostra el nom i guarda l'odoo_id a idClient -->
                    <v-autocomplete
  v-model="createForm.idClient"         
  :items="clientOptions"
  :loading="loadingClients"
  v-model:search="clientSearch"          
  @update:search="onClientSearch"        
  item-title="name"
  item-value="id"
  label="Cliente"
  clearable
  hide-details="auto"
  :return-object="false"                
  :error-messages="validationErrors.idClient"
/>

                  </v-col>

                  <!-- type: forçat per fixedType, no es demana al formulari -->

                  <v-col cols="12" sm="8">
                    <v-text-field
                      v-model="createForm.name"
                      label="Nombre"
                      :error-messages="validationErrors.name"
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-text-field
                      v-model="createForm.url"
                      label="Enlace"
                      :error-messages="validationErrors.url"
                    />
                  </v-col>

                  <!-- PUJADA D'IMATGE -->
                  <v-col cols="12" sm="6">
                    <v-file-input
                      v-model="imageFile"
                      label="Adjuntar imagen"
                      accept="image/*"
                      prepend-icon="mdi-image"
                      show-size
                      clearable
                    />
                  </v-col>

                  <!-- Preview (si hi ha URL o fitxer) -->
                  <v-col cols="12" v-if="previewSrc">
                    <div class="text-caption mb-2">Previsualización</div>
                    <v-img
                      :src="previewSrc"
                      height="180"
                      cover
                      class="rounded"
                    />
                  </v-col>

                  <v-col cols="12">
                    <v-textarea
                      v-model="createForm.description"
                      label="Descripción"
                      rows="3"
                      auto-grow
                      :error-messages="validationErrors.description"
                    />
                  </v-col>
                </v-row>
              </v-card-text>

              <v-card-actions>
                <v-spacer />
                <v-btn variant="text" @click="createDialog = false">Cancelar</v-btn>
                <v-btn color="primary" :loading="creating" @click="isEditing ? updateDocumento() : createDocumento()">
                  {{ isEditing ? 'Guardar cambios' : 'Crear' }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <!-- Modal borrar -->
          <v-dialog v-model="deleteDialog" max-width="480">
            <v-card>
              <v-card-title class="headline">Confirmar borrado</v-card-title>
              <v-card-text>
                ¿Quieres borrar el documento
                <strong v-if="deleteTarget">{{ deleteTarget.name }}</strong>?
              </v-card-text>
              <v-card-actions>
                <v-spacer />
                <v-btn variant="text" @click="deleteDialog = false">Cancelar</v-btn>
                <v-btn color="error" @click="performDelete">Borrar</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-col>
      </v-row>

      <!-- Buscador taula -->
      <v-row>
        <v-col cols="12" md="6">
          <v-text-field
            v-model="tableSearch"
            label="Buscar..."
            prepend-inner-icon="mdi-magnify"
            clearable
            density="compact"
          />
        </v-col>
      </v-row>

      <v-alert v-if="error" type="error" class="mb-3">{{ error }}</v-alert>
      <v-progress-linear v-if="loading" indeterminate class="mb-3" />

      <v-data-table
        :items="items"
        :headers="headers"
        :search="tableSearch"
        item-key="id"
        class="elevation-1"
      >
        <template #item.image="{ item }">
          <v-img
            v-if="item.image"
            :src="item.image"
            max-height="40"
            max-width="120"
            cover
            class="rounded"
          />
          <span v-else class="text-medium-emphasis">—</span>
        </template>

        <template #item.url="{ item }">
          <a :href="item.url" target="_blank" rel="noopener">{{ item.url }}</a>
        </template>

        <template #item.acciones="{ item }">
          <v-btn size="x-small" icon color="primary" class="mr-1" @click="openEdit(item)">
            <v-icon size="18">mdi-pencil</v-icon>
          </v-btn>
          <v-btn size="x-small" icon color="error" @click="confirmDelete(item)">
            <v-icon size="18">mdi-delete</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000" multi-line>
      {{ snackbarMessage }}
    </v-snackbar>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { useSnackbar } from '@/composables/useSnackbar'
import { useRoute } from 'vue-router'

function debounce(fn, wait = 300) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn(...args), wait)
  }
}

const { snackbar, snackbarMessage, snackbarColor, showSnackbar } = useSnackbar()

const API_PREFIX = '/api/admin'

const items = ref([])
const loading = ref(false)
const error = ref('')

// 🔹 Cerca de la taula (separat del buscador de clients)
const tableSearch = ref('')

const createDialog = ref(false)
const creating = ref(false)
const isEditing = ref(false)
const editId = ref(null)

const deleteDialog = ref(false)
const deleteTarget = ref(null)

const createForm = ref({
  idClient: null, // odoo_id del client seleccionat
  name: '',
  image: '', // URL opcional
  url: '',
  description: '',
})

const clientOptions = ref([])
const loadingClients = ref(false)
const clientSearch = ref('')

const imageFile = ref(null) // fitxer adjunt (si n'hi ha)

const validationErrors = ref({
  idClient: [],
  name: [],
  url: [],
  image: [],
  description: [],
})

const headers = [
  { title: 'ID', value: 'id', sortable: true },
  { title: 'Cliente', value: 'idClient', sortable: true }, // (opcional: canvia per client_name si el backend el retorna)
  { title: 'Tipo', value: 'type', sortable: true },
  { title: 'Nombre', value: 'name', sortable: true },
  { title: 'Imagen', value: 'image', sortable: false },
  { title: 'URL', value: 'url', sortable: false },
  { title: 'Descripción', value: 'description', sortable: false },
  { title: '', value: 'acciones', sortable: false },
]

const route = useRoute()
const fixedType = computed(() => {
  const raw = route.params.tipus
  if (raw === undefined || raw === null || raw === '') return null
  const n = Number(raw)
  return Number.isFinite(n) ? n : null
})

const NomType = ref('')

const token = localStorage.getItem('token')
if (token) {
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`
}

const fetchDocumentos = async () => {
  if (fixedType.value == 1) NomType.value = 'Estudios Geomarketing'
  else if (fixedType.value == 2) NomType.value = 'Calidad'
  else NomType.value = 'General'

  loading.value = true
  error.value = ''
  try {
    const params = {}
    if (fixedType.value !== null && fixedType.value !== undefined && fixedType.value !== '') {
      params.type = Number(fixedType.value)
    }

    const { data } = await axios.get(`${API_PREFIX}/documentacios`, {
      headers: { Accept: 'application/json' },
      params,
    })

    items.value = Array.isArray(data) ? data : []
  } catch (e) {
    console.error(e)
    error.value = e.response?.data?.message || e.message || 'Error cargando documentación'
  } finally {
    loading.value = false
  }
}

const numberOrNull = (v) => (v === '' || v === undefined || v === null ? null : Number(v))
const strOrNull = (v) => (v === undefined ? null : (v ?? null))
const ensureHttp = (u) => (!u ? u : (/^https?:\/\//i.test(u) ? u : `https://${u}`))
const clearValidation = () => Object.keys(validationErrors.value).forEach(k => (validationErrors.value[k] = []))

// Previsualització: si hi ha fitxer, fem ObjectURL; si no, usem la URL de text
const previewSrc = computed(() => {
  if (imageFile.value && imageFile.value instanceof File) {
    return URL.createObjectURL(imageFile.value)
  }
  return createForm.value.image || ''
})

const openCreateModal = () => {
  clearValidation()
  isEditing.value = false
  editId.value = null
  createForm.value = {
    idClient: null,
    name: '',
    image: '',
    url: '',
    description: '',
  }
  clientSearch.value = ''
  imageFile.value = null
  createDialog.value = true
}

const buildFormData = (base = {}) => {
  const fd = new FormData()
  // camps base
  Object.entries(base).forEach(([k, v]) => {
    if (v !== undefined && v !== null) fd.append(k, v)
  })
  // type forçat per URL
  if (fixedType.value !== null) fd.append('type', Number(fixedType.value))

  // imatge: si hi ha fitxer, l'enviem com 'image' (backend ha d'acceptar file)
  if (imageFile.value instanceof File) {
    fd.append('image', imageFile.value)
  } else if (createForm.value.image) {
    // si no hi ha fitxer però hi ha URL, la passem com a text
    fd.append('image', createForm.value.image)
  }
  return fd
}

const createDocumento = async () => {
  try {
    creating.value = true
    clearValidation()

    const base = {
      idClient: numberOrNull(createForm.value.idClient), // odoo_id
      name: strOrNull(createForm.value.name),
      url: strOrNull(createForm.value.url),
      description: strOrNull(createForm.value.description),
      // 'image' es resol dins buildFormData (fitxer o URL)
    }

    const formData = buildFormData(base)
console.log (formData)
    await axios.post(`${API_PREFIX}/documentacios`, formData, {
      headers: { Accept: 'application/json', 'Content-Type': 'multipart/form-data' },
    })

    await fetchDocumentos()
    createDialog.value = false
    showSnackbar('Documento creado correctamente', 'success')
  } catch (e) {
    if (e.response?.status === 422 && e.response?.data?.errors) {
      const errs = e.response.data.errors
      Object.keys(errs).forEach(k => {
        if (validationErrors.value[k] !== undefined) validationErrors.value[k] = errs[k]
      })
    } else {
      error.value = e.response?.data?.message || e.message || 'Error al crear'
    }
    showSnackbar('Error al crear el documento', 'error')
  } finally {
    creating.value = false
  }
}

const openEdit = (item) => {
  clearValidation()
  isEditing.value = true
  editId.value = item.id
  createForm.value = {
    idClient: item.idClient ?? null,  // odoo_id
    name: item.name ?? '',
    image: item.image ?? '',
    url: item.url ?? '',
    description: item.description ?? '',
  }
  clientSearch.value = ''
  imageFile.value = null // reinicia l'input de fitxer
  createDialog.value = true
}

const updateDocumento = async () => {
  creating.value = true
  clearValidation()
  try {
    const base = {
      idClient: createForm.value.idClient == null ? undefined : Number(createForm.value.idClient),
      name: createForm.value.name?.trim() || undefined,
      url: createForm.value.url ? ensureHttp(createForm.value.url.trim()) : undefined,
      description: (createForm.value.description?.trim() || '') || undefined,
      // 'type' i 'image' (fitxer/URL) els resol buildFormData
    }

    const formData = buildFormData(base)

    const { data } = await axios.post(
      `${API_PREFIX}/documentacios/${editId.value}?_method=PATCH`,
      formData,
      { headers: { Accept: 'application/json', 'Content-Type': 'multipart/form-data' } }
    )

    const idx = items.value.findIndex(i => i.id === editId.value)
    if (idx >= 0) items.value[idx] = data

    createDialog.value = false
    showSnackbar('Documento actualizado correctamente', 'success')
  } catch (e) {
    if (e.response?.status === 422 && e.response?.data?.errors) {
      const errs = e.response.data.errors
      Object.keys(errs).forEach(k => {
        if (validationErrors.value[k] !== undefined) validationErrors.value[k] = errs[k]
      })
    } else {
      error.value = e.response?.data?.message || e.message || 'Error al actualizar'
    }
    showSnackbar('Error al actualizar', 'error')
  } finally {
    creating.value = false
  }
}

const confirmDelete = (item) => {
  deleteTarget.value = item
  deleteDialog.value = true
}

const performDelete = async () => {
  try {
    await axios.delete(`${API_PREFIX}/documentacios/${deleteTarget.value.id}`, {
      headers: { Accept: 'application/json' },
    })
    items.value = items.value.filter(i => i.id !== deleteTarget.value.id)
    deleteDialog.value = false
    showSnackbar('Documento borrado', 'success')
  } catch (e) {
    console.error('Error eliminando documento:', e)
    showSnackbar('Error al borrar', 'error')
  }
}

// 🔹 Opcions de client (per al desplegable)
const fetchClientOptions = async (q = '') => {
  loadingClients.value = true
  try {
    const url = `${API_PREFIX}/clients/llistatIdOdoo` // si NO és admin: '/api/clients/llistatIdOdoo'
    const { data } = await axios.get(url, {
      headers: { Accept: 'application/json' },
      params: { q, limit: 50 },
    })
    const payload = Array.isArray(data) ? data : (data?.data ?? data)
    clientOptions.value = Array.isArray(payload) ? payload : [payload]
  } catch (e) {
    console.error('Error cargando clientes', e)
    showSnackbar(e.response?.data?.message || 'Error cargando clientes', 'error')
  } finally {
    loadingClients.value = false
  }
}


// 🔹 Debounce de la cerca del desplegable
const onClientSearch = debounce((val) => {
  clientSearch.value = val || ''
  fetchClientOptions(clientSearch.value)
}, 300)

// Carrega inicial
onMounted(() => {
  fetchClientOptions('')
  fetchDocumentos()
})
</script>

<style scoped>
/* opcional: estil de preview etc. */
</style>
